import { Button, Loader, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityQuestRO } from '@routellers/types/dist/city-quest/city-quest.ro';
import { CityQuestLocaleStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-locale-status.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';
import { EditorFooter, LanguageChips, Section } from '../../components/editor';
import { enumOptions } from '../../i18n';
import { contentLanguagesForCountry } from '../../utils/content-languages';

type Props = {
    quest: CityQuestRO;
    onUpdated: (quest: CityQuestRO) => void;
    onClose: () => void;
    onDirtyChange: (dirty: boolean) => void;
};

type CatalogDraft = {
    title: string;
    tagline: string;
    voicedBy: string;
    status: CityQuestLocaleStatusEnum;
};

const catalogDraftOf = (quest: CityQuestRO, language: Languages): CatalogDraft => {
    const locale = quest.locales?.find((row) => row.language === language);

    return {
        title: locale?.title || '',
        tagline: locale?.tagline || '',
        voicedBy: locale?.voicedBy || '',
        status: locale?.status || CityQuestLocaleStatusEnum.EMPTY,
    };
};

/** Catalog copy only. Prologue / finale live on Constructor as fixed bookends. */
export const QuestLocalesTab = ({ quest, onUpdated, onClose, onDirtyChange }: Props) => {
    const { t } = useTranslation();
    const localeStatusOptions = useMemo(
        () => enumOptions(Object.values(CityQuestLocaleStatusEnum), t, 'enums.localeStatus'),
        [t],
    );
    const contentLanguages = useMemo(
        () => contentLanguagesForCountry(quest.country),
        [quest.country],
    );
    const [language, setLanguage] = useState<Languages>(
        contentLanguages.includes(quest.localePrimary)
            ? quest.localePrimary
            : contentLanguages[0] || quest.localePrimary,
    );
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState<CatalogDraft>(() => catalogDraftOf(quest, language));
    const [baseline, setBaseline] = useState(() => JSON.stringify(catalogDraftOf(quest, language)));

    const dirty = JSON.stringify(draft) !== baseline;

    useEffect(() => {
        onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);

    useEffect(() => {
        const next = catalogDraftOf(quest, language);

        setDraft(next);
        setBaseline(JSON.stringify(next));
    }, [quest, language]);

    const patchQuest = useCallback((patch: Partial<CatalogDraft>) => {
        setDraft((d) => ({ ...d, ...patch }));
    }, []);

    const chips = contentLanguages.map((lang) => {
        const locale = quest.locales?.find((row) => row.language === lang);
        let mark: 'empty' | 'filled' | 'reviewed' | 'dirty' = 'empty';

        if (lang === language && dirty) mark = 'dirty';
        else if (locale?.status === CityQuestLocaleStatusEnum.REVIEWED) mark = 'reviewed';
        else if (locale?.title) mark = 'filled';

        return { value: lang, label: lang.toUpperCase(), mark };
    });

    const save = async (status?: CityQuestLocaleStatusEnum) => {
        if (!draft.title.trim()) {
            notifications.show({ color: 'red', message: t('editors.quest.locales.titleRequired') });

            return;
        }

        setSaving(true);

        try {
            await api.cityQuestsApi.upsertLocale({
                id: quest.id,
                data: {
                    language,
                    title: draft.title.trim(),
                    tagline: draft.tagline.trim() || null,
                    voicedBy: draft.voicedBy.trim() || null,
                    status: status || draft.status,
                },
            });

            const { data: refreshed } = await api.cityQuestsApi.getOne(quest.id);

            if (refreshed) onUpdated(refreshed);
            notifications.show({ color: 'green', message: t('editors.quest.locales.saved') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    if (!draft) return <Loader />;

    return (
        <Stack gap='lg' pb={40}>
            <LanguageChips
                languages={chips}
                value={language}
                onChange={(v) => setLanguage(v as Languages)}
            />

            <Section
                boxed
                title={t('editors.quest.locales.catalogCopy.title')}
                description={t('editors.quest.locales.catalogCopy.description')}
            >
                <TextInput
                    label={t('editors.quest.locales.catalogCopy.titleField')}
                    value={draft.title}
                    onChange={(e) => patchQuest({ title: e.currentTarget.value })}
                />
                <Textarea
                    label={t('editors.quest.locales.catalogCopy.tagline')}
                    minRows={2}
                    autosize
                    value={draft.tagline}
                    onChange={(e) => patchQuest({ tagline: e.currentTarget.value })}
                />
                <TextInput
                    label={t('editors.quest.locales.catalogCopy.voicedBy')}
                    description={t('editors.quest.locales.catalogCopy.voicedByHint')}
                    value={draft.voicedBy}
                    onChange={(e) => patchQuest({ voicedBy: e.currentTarget.value })}
                />
                <Select
                    label={t('editors.quest.locales.catalogCopy.localeStatus')}
                    data={localeStatusOptions}
                    value={draft.status}
                    onChange={(v) =>
                        patchQuest({
                            status: (v as CityQuestLocaleStatusEnum) || draft.status,
                        })
                    }
                />
            </Section>

            <EditorFooter
                onCancel={onClose}
                onSave={() => save()}
                saving={saving}
                saveLabel={t('editors.quest.locales.saveLocale')}
                left={
                    <Button
                        variant='light'
                        color='green'
                        loading={saving}
                        onClick={() => save(CityQuestLocaleStatusEnum.REVIEWED)}
                    >
                        {t('editors.quest.locales.saveMarkReviewed')}
                    </Button>
                }
            />
        </Stack>
    );
};
